import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Play, Video, Upload, Youtube, Circle, Square } from "lucide-react";

export default function ExerciseLibrary() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", muscle: "", video_url: "" });
  const [videoModal, setVideoModal] = useState(null);
  const [videoSource, setVideoSource] = useState("youtube"); // youtube | upload | record
  const [recording, setRecording] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const load = async () => setItems((await api.get("/exercise-library")).data);
  useEffect(() => { load(); }, []);
  useEffect(() => () => { streamRef.current?.getTracks().forEach(t => t.stop()); }, []);

  const ytToEmbed = (url) => {
    if (url.includes("/embed/")) return url;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|v\/))([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: "video/webm" });
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const reader = new FileReader();
        reader.onload = () => {
          setForm(f => ({ ...f, video_url: reader.result }));
          setPreviewUrl(reader.result);
          toast.success("Gravação salva — pronta para anexar");
        };
        reader.readAsDataURL(blob);
        streamRef.current?.getTracks().forEach(t => t.stop());
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      // Auto-stop after 30s
      setTimeout(() => { if (rec.state === "recording") { rec.stop(); setRecording(false); } }, 30000);
    } catch { toast.error("Sem acesso à câmera/microfone"); }
  };
  const stopRec = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const onUpload = (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error("Arquivo maior que 30MB");
    const reader = new FileReader();
    reader.onload = () => { setForm(f => ({ ...f, video_url: reader.result })); setPreviewUrl(reader.result); toast.success("Vídeo carregado"); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim() || !form.muscle.trim()) return toast.error("Preencha nome e grupo muscular");
    let url = form.video_url;
    if (videoSource === "youtube") {
      url = ytToEmbed(url.trim());
      if (!url) return toast.error("URL do YouTube inválida");
    }
    if (!url) return toast.error("Anexe um vídeo (YouTube, upload ou gravação)");
    try {
      await api.post("/exercise-library", { name: form.name.trim(), muscle: form.muscle.trim(), video_url: url });
      toast.success("Exercício adicionado à biblioteca");
      setOpen(false); setForm({ name: "", muscle: "", video_url: "" }); setPreviewUrl(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover exercício da biblioteca?")) return;
    await api.delete(`/exercise-library/${id}`); toast.success("Removido"); load();
  };

  const isYouTube = (u) => u && (u.includes("youtube.com") || u.includes("youtu.be"));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Biblioteca de Exercícios"
        subtitle={`${items.length} exercícios · ${items.filter(i=>i.custom).length} personalizados da academia`}
        actions={
          <Button onClick={()=>setOpen(true)} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="library-add-button"><Plus className="w-4 h-4 mr-2"/>Novo exercício</Button>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-grid">
        {items.map((e, i) => (
          <Card key={e.id || i} className="p-4 border border-slate-200 hover:border-rose-300 transition-colors group">
            <div className="flex items-start gap-3">
              <button onClick={()=>setVideoModal({ url: e.video_url, title: e.name })} className="w-14 h-14 rounded-xl bg-rose-100 hover:bg-rose-200 flex items-center justify-center shrink-0 transition-colors"><Play className="w-6 h-6 text-rose-600"/></button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-slate-900 truncate">{e.name}</div>
                  {e.custom && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase">CUSTOM</span>}
                </div>
                <div className="text-xs text-slate-500">{e.muscle}</div>
                {e.added_by && <div className="text-[10px] text-slate-400 mt-1">por {e.added_by}</div>}
              </div>
              {e.custom && <Button variant="ghost" size="icon" onClick={()=>remove(e.id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4 text-rose-400"/></Button>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><Video className="w-5 h-5 text-rose-600"/>Novo exercício</h3>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Ex: Agachamento búlgaro" data-testid="library-name-input"/></div>
            <div><Label>Grupo muscular</Label><Input value={form.muscle} onChange={(e)=>setForm({...form,muscle:e.target.value})} placeholder="Pernas, Peito..." data-testid="library-muscle-input"/></div>
          </div>
          <div className="flex gap-1 border-b border-slate-200 mb-4">
            {[{ k: "youtube", l: "YouTube", i: Youtube }, { k: "upload", l: "Upload", i: Upload }, { k: "record", l: "Gravar aqui", i: Circle }].map(t => {
              const Icon = t.i; const active = videoSource === t.k;
              return <button key={t.k} onClick={()=>setVideoSource(t.k)} className={`px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-b-2 flex items-center gap-1.5 ${active ? "text-rose-600 border-rose-600" : "text-slate-500 border-transparent"}`} data-testid={`library-source-${t.k}`}><Icon className="w-4 h-4"/>{t.l}</button>;
            })}
          </div>

          {videoSource === "youtube" && (
            <Input value={form.video_url} onChange={(e)=>setForm({...form,video_url:e.target.value})} placeholder="https://www.youtube.com/watch?v=..." data-testid="library-yt-input"/>
          )}
          {videoSource === "upload" && (
            <div className="rounded-xl border-2 border-dashed border-slate-300 p-6 text-center">
              <Upload className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
              <input type="file" accept="video/*" onChange={(e)=>onUpload(e.target.files?.[0])} id="lib-upload" className="hidden" data-testid="library-upload-input"/>
              <Button asChild className="bg-rose-600 hover:bg-rose-700"><label htmlFor="lib-upload" className="cursor-pointer">Escolher vídeo (máx 30MB)</label></Button>
              {previewUrl && <video src={previewUrl} controls className="w-full mt-3 rounded-lg" style={{maxHeight: 240}}/>}
            </div>
          )}
          {videoSource === "record" && (
            <div className="space-y-2">
              <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover"/>
                {recording && <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-rose-600 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"/>REC · Máx 30s</div>}
              </div>
              <div className="flex gap-2">
                {!recording ? (
                  <Button onClick={startRec} className="flex-1 bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="library-record-start"><Circle className="w-4 h-4 mr-2 fill-current"/>Iniciar gravação</Button>
                ) : (
                  <Button onClick={stopRec} className="flex-1 bg-slate-900 hover:bg-black font-bold uppercase" data-testid="library-record-stop"><Square className="w-4 h-4 mr-2 fill-current"/>Parar</Button>
                )}
              </div>
              {previewUrl && !recording && <video src={previewUrl} controls className="w-full rounded-lg" style={{maxHeight: 240}}/>}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="library-save"><Plus className="w-4 h-4 mr-2"/>Adicionar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!videoModal} onOpenChange={(v)=>!v && setVideoModal(null)}>
        <DialogContent className="max-w-3xl">
          <h3 className="font-heading text-lg font-bold mb-3">{videoModal?.title}</h3>
          {videoModal && (isYouTube(videoModal.url) ? (
            <div className="aspect-video bg-black rounded-lg overflow-hidden"><iframe src={videoModal.url} title="v" className="w-full h-full" allowFullScreen/></div>
          ) : <video src={videoModal.url} controls autoPlay className="w-full rounded-lg bg-black" style={{maxHeight:"70vh"}}/>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
